import { makeAutoObservable } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { peekQueryClient } from "@/queries/client";
import { GLOBAL_TENANT } from "@/queries/scope";
import { myWorkHoursKey } from "@/queries/work-hours";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type { TUserWorkHours } from "@/types/user";

/**
 * The signed-in user's work hours write. The hours themselves are read only
 * through Query (`useMyWorkHours`, NG-2814); the write patches that entry so
 * the profile page, the Timeline and the Events pages show it at once.
 */
export class WorkHoursStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /**
   * Save `patch`. Optimistic: the cached hours change now, go back on failure,
   * and take the server's answer on success. A read in flight is cancelled
   * first, so an answer sent before the change cannot land over it.
   */
  async updateWorkHours(
    patch: Partial<TUserWorkHours>,
    source: AnalyticsSource = "page",
  ): Promise<TUserWorkHours> {
    const scope = queryCacheScope(this.rootStore, GLOBAL_TENANT, "work-hours", null);
    const client = peekQueryClient();
    const key = scope ? myWorkHoursKey(scope) : null;
    const previous = client && key ? client.getQueryData<TUserWorkHours>(key) : undefined;
    // Read before the optimistic patch below: `fields` is what this save changed.
    const fields = changedFields(patch, previous);
    if (client && key && previous) {
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData<TUserWorkHours>(key, { ...previous, ...patch });
    }
    try {
      const updated = await this.rootStore.workHoursService.updateCurrentUserWorkHours(patch);
      if (client && key) client.setQueryData<TUserWorkHours>(key, updated);
      if (fields.length > 0) track("work_hours_updated", { fields, source });
      return updated;
    } catch (error) {
      if (client && key && previous) client.setQueryData<TUserWorkHours>(key, previous);
      throw error;
    }
  }
}
